import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { createAuth0Client, type Auth0Client } from "@auth0/auth0-spa-js";
import {
  AdminApiPath,
  AdminAuthConfigSchema,
  AdminSessionSchema,
  type AdminSession,
} from "@toy/contracts";
import {
  ActivityIndicator,
  ScrollView,
  Text,
  View,
  useWindowDimensions,
} from "react-native";
import { useTranslation } from "react-i18next";
import { AdminApiClient, AdminApiError } from "./api";
import { Action, Choice, colors, ui } from "./components";

const AppsPage = lazy(() =>
  import("./pages/AppsPage").then((module) => ({ default: module.AppsPage })),
);
const MarketPage = lazy(() =>
  import("./pages/MarketPage").then((module) => ({
    default: module.MarketPage,
  })),
);
const AuditPage = lazy(() =>
  import("./pages/AuditPage").then((module) => ({ default: module.AuditPage })),
);
const EntitlementsPage = lazy(() =>
  import("./pages/EntitlementsPage").then((module) => ({
    default: module.EntitlementsPage,
  })),
);
const OperationsPage = lazy(() =>
  import("./pages/OperationsPage").then((module) => ({
    default: module.OperationsPage,
  })),
);
const OverviewPage = lazy(() =>
  import("./pages/OverviewPage").then((module) => ({
    default: module.OverviewPage,
  })),
);
const SettingsPage = lazy(() =>
  import("./pages/SettingsPage").then((module) => ({
    default: module.SettingsPage,
  })),
);
const UsersPage = lazy(() =>
  import("./pages/UsersPage").then((module) => ({ default: module.UsersPage })),
);

const platformRoutes = [
  {
    key: "overview",
    path: "/overview",
    permission: "platform.overview.read",
  },
  {
    key: "market",
    path: "/market",
    permission: "platform.market.read",
  },
  {
    key: "users",
    path: "/users",
    permission: "platform.users.read",
  },
  {
    key: "entitlements",
    path: "/entitlements",
    permission: "platform.entitlements.read",
  },
  {
    key: "operations",
    path: "/operations",
    permission: "platform.jobs.read",
  },
  {
    key: "audit",
    path: "/audit",
    permission: "platform.audit.read",
  },
  {
    key: "settings",
    path: "/settings",
    permission: "platform.settings.manage",
  },
] as const;
const appRoutes = [
  {
    key: "apps",
    path: "/apps",
    permission: "app.overview.read",
  },
] as const;
const routeGroups = [
  { key: "platform", routes: platformRoutes },
  { key: "app", routes: appRoutes },
] as const;
const routes = [...platformRoutes, ...appRoutes] as const;

type RouteKey = (typeof routes)[number]["key"];
type AppState =
  | { status: "thinking" }
  | { status: "signed-out"; auth: Auth0Client }
  | {
      status: "ready";
      auth: Auth0Client;
      api: AdminApiClient;
      session: AdminSession;
    }
  | { status: "error"; auth?: Auth0Client; error: unknown };

function currentRoute(): RouteKey {
  const path = window.location.pathname;
  return routes.find((route) => route.path === path)?.key ?? "overview";
}

export function App() {
  const { t } = useTranslation();
  const { width, height } = useWindowDimensions();
  const wide = width >= 1024 && height >= 600;
  const [state, setState] = useState<AppState>({ status: "thinking" });
  const [route, setRoute] = useState<RouteKey>(currentRoute);
  const apiBase = (
    import.meta.env.DEV ? "/api" : import.meta.env.VITE_ADMIN_API_BASE || "/api"
  ).replace(/\/+$/, "");

  useEffect(() => {
    const onPopState = () => setRoute(currentRoute());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    let active = true;
    async function initialize() {
      try {
        const response = await fetch(`${apiBase}${AdminApiPath.AuthConfig}`);
        const config = AdminAuthConfigSchema.parse(await response.json());
        const auth = await createAuth0Client({
          domain: config.domain,
          clientId: config.clientId,
          cacheLocation: "memory",
          useRefreshTokens: false,
          authorizationParams: {
            audience: config.audience,
            scope: config.scope,
            redirect_uri: window.location.origin,
          },
        });
        if (
          window.location.search.includes("code=") &&
          window.location.search.includes("state=")
        ) {
          await auth.handleRedirectCallback();
          window.history.replaceState({}, "", "/overview");
          setRoute("overview");
        }
        if (!(await auth.isAuthenticated())) {
          if (active) setState({ status: "signed-out", auth });
          return;
        }
        const api = new AdminApiClient(apiBase, auth);
        const session = await api.get(AdminApiPath.Session, AdminSessionSchema);
        if (window.location.pathname === "/") {
          window.history.replaceState({}, "", "/overview");
          setRoute("overview");
        }
        if (active) setState({ status: "ready", auth, api, session });
      } catch (error) {
        if (active) setState({ status: "error", error });
      }
    }
    void initialize();
    return () => {
      active = false;
    };
  }, [apiBase]);

  const availableRouteGroups = useMemo(() => {
    if (state.status !== "ready") return [];
    return routeGroups
      .map((group) => ({
        ...group,
        routes: group.routes.filter((candidate) =>
          state.session.grants.some(
            (grant) =>
              grant.permissions.includes(candidate.permission) &&
              (group.key === "app" || grant.scope.type === "platform"),
          ),
        ),
      }))
      .filter((group) => group.routes.length > 0);
  }, [state]);

  const activeRoute = useMemo(
    () =>
      availableRouteGroups
        .flatMap((group) => group.routes)
        .find((candidate) => candidate.key === route) ??
      availableRouteGroups[0]?.routes[0],
    [availableRouteGroups, route],
  );

  useEffect(() => {
    if (!activeRoute || activeRoute.key === route) return;
    window.history.replaceState({}, "", activeRoute.path);
    setRoute(activeRoute.key);
  }, [activeRoute, route]);

  function navigate(next: (typeof routes)[number]) {
    window.history.pushState({}, "", next.path);
    setRoute(next.key);
  }

  const content = useMemo(() => {
    if (state.status !== "ready") return null;
    if (activeRoute?.key === "market") return <MarketPage api={state.api} />;
    if (activeRoute?.key === "apps") return <AppsPage api={state.api} />;
    if (activeRoute?.key === "users") return <UsersPage api={state.api} />;
    if (activeRoute?.key === "entitlements")
      return <EntitlementsPage api={state.api} />;
    if (activeRoute?.key === "operations")
      return <OperationsPage api={state.api} />;
    if (activeRoute?.key === "audit") return <AuditPage api={state.api} />;
    if (activeRoute?.key === "settings")
      return <SettingsPage session={state.session} />;
    return <OverviewPage api={state.api} />;
  }, [activeRoute, state]);

  if (state.status === "thinking")
    return (
      <Centered
        icon={<ActivityIndicator color={colors.accent} />}
        title={t("common.thinking")}
      />
    );
  if (state.status === "signed-out")
    return (
      <Centered
        title={t("productName")}
        body={t("productDescription")}
        action={
          <Action
            label={t("common.signIn")}
            tone="primary"
            onPress={() => void state.auth.loginWithRedirect()}
          />
        }
      />
    );
  if (state.status === "error") {
    const denied =
      state.error instanceof AdminApiError && state.error.status === 403;
    return (
      <Centered
        title={denied ? t("common.accessDenied") : t("common.unavailable")}
        body={
          denied ? t("common.strongAuthRequired") : t("common.requestFailed")
        }
        action={
          <Action
            label={t("common.retry")}
            onPress={() => window.location.reload()}
          />
        }
      />
    );
  }
  return (
    <View
      style={{
        flex: 1,
        flexDirection: wide ? "row" : "column",
        backgroundColor: colors.background,
      }}
    >
      {wide ? (
        <View
          style={{
            width: 240,
            padding: 16,
            gap: 28,
            backgroundColor: colors.navigation,
            borderRightWidth: 1,
            borderColor: colors.border,
          }}
        >
          <View style={{ gap: 4 }}>
            <Text accessibilityRole="header" style={ui.heading}>
              {t("productName")}
            </Text>
            <Text style={ui.muted}>{t("common.appCount")}</Text>
          </View>
          <ScrollView contentContainerStyle={ui.stack}>
            {availableRouteGroups.map((group) => (
              <View key={group.key} style={{ gap: 8 }}>
                <Text style={ui.label}>{t(`navGroups.${group.key}`)}</Text>
                {group.routes.map((candidate) => (
                  <Action
                    key={candidate.key}
                    label={t(`nav.${candidate.key}`)}
                    tone="quiet"
                    selected={activeRoute?.key === candidate.key}
                    style={{ alignSelf: "stretch" }}
                    onPress={() => navigate(candidate)}
                  />
                ))}
              </View>
            ))}
          </ScrollView>
          <Action
            label={t("common.signOut")}
            tone="quiet"
            onPress={() =>
              void state.auth.logout({
                logoutParams: { returnTo: window.location.origin },
              })
            }
          />
        </View>
      ) : (
        <View
          style={[
            ui.row,
            { padding: 12, borderBottomWidth: 1, borderColor: colors.border },
          ]}
        >
          <Text style={ui.heading}>{t("productName")}</Text>
          <Choice
            label={t("common.navigation")}
            value={activeRoute?.key}
            onChange={(value) => {
              const next = routes.find((candidate) => candidate.key === value);
              if (next) navigate(next);
            }}
            options={availableRouteGroups.flatMap((group) =>
              group.routes.map((candidate) => ({
                value: candidate.key,
                label: `${t(`navGroups.${group.key}`)} · ${t(`nav.${candidate.key}`)}`,
              })),
            )}
          />
          <Action
            label={t("common.signOut")}
            tone="quiet"
            onPress={() =>
              void state.auth.logout({
                logoutParams: { returnTo: window.location.origin },
              })
            }
          />
        </View>
      )}
      <ScrollView
        style={{ flex: 1, minWidth: 0 }}
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{ padding: wide ? 32 : 16, gap: 20 }}
      >
        <Suspense
          fallback={
            <View style={ui.row}>
              <ActivityIndicator color={colors.accent} />
              <Text style={ui.muted}>{t("common.thinking")}</Text>
            </View>
          }
        >
          {content}
        </Suspense>
      </ScrollView>
    </View>
  );
}

function Centered({
  icon,
  title,
  body,
  action,
}: {
  icon?: React.ReactNode;
  title: string;
  body?: string;
  action?: React.ReactNode;
}) {
  return (
    <View
      style={{
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        backgroundColor: colors.background,
      }}
    >
      <View
        style={[
          ui.panel,
          { width: "100%", maxWidth: 440, padding: 28, gap: 20 },
        ]}
      >
        {icon}
        <Text
          accessibilityRole="header"
          style={[ui.heading, { fontSize: 24, lineHeight: 32, letterSpacing: -0.4 }]}
        >
          {title}
        </Text>
        {body ? <Text style={ui.text}>{body}</Text> : null}
        {action ? <View>{action}</View> : null}
      </View>
    </View>
  );
}
